import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogTrigger
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MarkdownField } from "@/components/ui/markdown-field";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useBibleData } from "@/hooks/use-bible-data";
import { useAuth } from "@/hooks/use-auth";
import { BookText, Loader2, StickyNote } from "lucide-react";
import { api } from "@/lib/api";

interface AddNoteModalProps {
  documentId?: number;
  triggerElement?: React.ReactNode;
  buttonClassName?: string;
}

type NoteType = 'standalone' | 'scripture' | 'quote' | 'document';

export function AddNoteModal({ 
  documentId, 
  triggerElement, 
  buttonClassName 
}: AddNoteModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { books, allBookNames, isLoading: isBibleDataLoading, getBookChapters, getChapterVerses } = useBibleData();
  
  const [open, setOpen] = useState(false);
  const [noteType, setNoteType] = useState<NoteType>('standalone');
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Bible reference selection state
  const [selectedBook, setSelectedBook] = useState<string>("");
  const [selectedChapter, setSelectedChapter] = useState<number>(1);
  const [verseStart, setVerseStart] = useState<number>(1);
  const [verseEnd, setVerseEnd] = useState<number>(1);
  
  // Derived values
  const chaptersInBook = selectedBook ? getBookChapters(selectedBook) : 0;
  const versesInChapter = selectedBook && selectedChapter 
    ? getChapterVerses(selectedBook, selectedChapter) 
    : 0;
  
  // Options for select dropdowns
  const chapterOptions = Array.from({ length: chaptersInBook }, (_, i) => i + 1);
  const verseOptions = Array.from({ length: versesInChapter }, (_, i) => i + 1);
  
  const resetForm = () => {
    setNote("");
    setNoteType('standalone');
    setSelectedBook("");
    setSelectedChapter(1);
    setVerseStart(1);
    setVerseEnd(1);
  };
  
  const handleVerseStartChange = (value: string) => {
    const start = parseInt(value);
    setVerseStart(start);
    
    // Ensure verse end is not less than verse start
    if (verseEnd < start) {
      setVerseEnd(start);
    }
  };
  
  const handleVerseEndChange = (value: string) => {
    const end = parseInt(value);
    setVerseEnd(end);
  };
  
  const handleBookChange = (value: string) => {
    setSelectedBook(value);
    setSelectedChapter(1);
    setVerseStart(1);
    setVerseEnd(1);
  };
  
  const handleChapterChange = (value: string) => {
    const chapter = parseInt(value);
    setSelectedChapter(chapter);
    setVerseStart(1);
    setVerseEnd(1);
  };
  
  const isValidReference = () => {
    if (noteType !== 'scripture') return true;
    
    return (
      selectedBook && 
      selectedChapter > 0 && 
      selectedChapter <= chaptersInBook &&
      verseStart > 0 && 
      verseStart <= versesInChapter &&
      verseEnd >= verseStart && 
      verseEnd <= versesInChapter
    );
  };
  
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };
  
  const handleSubmit = async () => {
    if (!note.trim()) {
      toast({
        title: "Note is empty",
        description: "Please enter a note to save.",
        variant: "destructive",
      });
      return;
    }

    if (noteType === 'scripture' && !isValidReference()) {
      toast({
        title: "Invalid reference",
        description: "Please select a valid scripture reference.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const noteData: {
        content: string;
        parent_type: NoteType;
        parent_id?: number;
        scripture_reference?: {
          book: string;
          chapter: number;
          verse_start: number;
          verse_end: number;
        };
      } = {
        content: note,
        parent_type: noteType,
      };

      if (noteType === 'scripture') {
        noteData.scripture_reference = {
          book: selectedBook,
          chapter: selectedChapter,
          verse_start: verseStart,
          verse_end: verseEnd
        };
      } else if (noteType === 'document' && documentId) {
        noteData.parent_id = documentId;
      }
      
      // Post to the API endpoint
      await api("/notes", {
        method: "POST",
        body: noteData
      });

      // The saved note has to appear where the user is looking. Without this
      // the notes list and the dashboard kept showing their mount-time fetch,
      // so a note saved fine and then seemed not to exist until a reload.
      await queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
      if (noteType === "scripture") {
        await queryClient.invalidateQueries({
          queryKey: ["/api/scripture-references"],
        });
      }

      toast({
        title: "Note saved",
        description: "Your note has been saved successfully.",
      });
      
      // Reset form and close modal
      resetForm();
      setOpen(false);
    } catch (err) {
      console.error("Error saving note:", err);
      toast({
        title: "Error saving note",
        description: "There was a problem saving your note. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatReference = () => {
    if (!selectedBook) return "";
    
    const baseRef = `${selectedBook} ${selectedChapter}`;
    
    if (!verseStart) return baseRef;
    
    if (verseStart === verseEnd) {
      return `${baseRef}:${verseStart}`;
    }
    
    return `${baseRef}:${verseStart}-${verseEnd}`;
  };
  
  const referenceString = formatReference();
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerElement || (
          <Button variant="outline" className={buttonClassName}>
            <StickyNote className="mr-2 h-4 w-4" />
            Add Note
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>
            Create a new note. Choose the type of note you want to create.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          {/* Note Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="note-type">Note Type</Label>
            <Select value={noteType} onValueChange={(value: NoteType) => setNoteType(value)}>
              <SelectTrigger id="note-type">
                <SelectValue placeholder="Select note type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standalone">Standalone Note</SelectItem>
                <SelectItem value="scripture">Scripture Note</SelectItem>
                <SelectItem value="quote">Quote Note</SelectItem>
                <SelectItem value="document">Document Note</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bible Reference Selection - Only show for scripture notes */}
          {noteType === 'scripture' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="book">Book</Label>
                <Select value={selectedBook} onValueChange={handleBookChange}>
                  <SelectTrigger id="book">
                    <SelectValue placeholder="Select a book" />
                  </SelectTrigger>
                  <SelectContent>
                    {allBookNames.map((book) => (
                      <SelectItem key={book} value={book}>
                        {book}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedBook && (
                <div>
                  <Label htmlFor="chapter">Chapter</Label>
                  <Select 
                    value={selectedChapter.toString()} 
                    onValueChange={handleChapterChange}
                    disabled={chapterOptions.length === 0}
                  >
                    <SelectTrigger id="chapter">
                      <SelectValue placeholder="Select a chapter" />
                    </SelectTrigger>
                    <SelectContent>
                      {chapterOptions.map((chapter) => (
                        <SelectItem key={chapter} value={chapter.toString()}>
                          {chapter}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {selectedBook && selectedChapter > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="verse-start">Verse Start</Label>
                    <Select 
                      value={verseStart.toString()} 
                      onValueChange={handleVerseStartChange}
                      disabled={verseOptions.length === 0}
                    >
                      <SelectTrigger id="verse-start">
                        <SelectValue placeholder="Start verse" />
                      </SelectTrigger>
                      <SelectContent>
                        {verseOptions.map((verse) => (
                          <SelectItem key={`start-${verse}`} value={verse.toString()}>
                            {verse}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="verse-end">Verse End</Label>
                    <Select 
                      value={verseEnd.toString()} 
                      onValueChange={handleVerseEndChange}
                      disabled={verseOptions.length === 0 || !verseStart}
                    >
                      <SelectTrigger id="verse-end">
                        <SelectValue placeholder="End verse" />
                      </SelectTrigger>
                      <SelectContent>
                        {verseOptions
                          .filter((v) => v >= verseStart)
                          .map((verse) => (
                            <SelectItem key={`end-${verse}`} value={verse.toString()}>
                              {verse}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              {referenceString && (
                <div className="mt-2 p-2 bg-muted rounded-md">
                  <p className="text-sm font-medium">Selected Reference: {referenceString}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Note Input */}
          <div className="space-y-2">
            <Label htmlFor="note">Your Note</Label>
            <MarkdownField
              id="note"
              placeholder="Add your thoughts or insights..."
              value={note}
              onValueChange={setNote}
            />
          </div>
        </div>
        
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="secondary"
            onClick={() => setOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !note.trim() || !isValidReference()}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Note"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 